import { useEffect } from "react";
import { useLocation } from "react-router-dom";

type PlausibleFn = (
  eventName: string,
  options?: { props?: Record<string, string | number>; callback?: () => void },
) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

const DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;
const SCRIPT_SRC = import.meta.env.VITE_PLAUSIBLE_SCRIPT as string | undefined;
const STORAGE_KEY = "casinomenu.cookieConsent";

function hasConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "accepted";
  } catch {
    return false;
  }
}

/**
 * Plausible-compatible analytics that loads only when:
 *   1. VITE_PLAUSIBLE_DOMAIN is configured, AND
 *   2. The user has accepted analytics in the cookie banner.
 *
 * Cookieless by default — no PII leaves the browser. Counterpart-compatible
 * scripts (e.g. cabin.so, simpleanalytics.com) can be wired by setting
 * VITE_PLAUSIBLE_SCRIPT to their endpoint.
 */
export function Analytics() {
  const location = useLocation();

  // Inject script once consent + config are present.
  useEffect(() => {
    if (!DOMAIN || !hasConsent()) return;
    if (document.getElementById("cm-analytics")) return;

    const s = document.createElement("script");
    s.id = "cm-analytics";
    s.defer = true;
    s.dataset.domain = DOMAIN;
    s.src = SCRIPT_SRC || "https://plausible.io/js/script.js";
    document.head.appendChild(s);

    // Lightweight queue stub so calls before script-ready don't blow up.
    if (!window.plausible) {
      window.plausible = function queued(...args: unknown[]) {
        (window.plausible as unknown as { q?: unknown[] }).q =
          (window.plausible as unknown as { q?: unknown[] }).q || [];
        ((window.plausible as unknown as { q: unknown[] }).q).push(args);
      } as PlausibleFn;
    }
  }, []);

  // Re-emit pageview on every client-side navigation.
  useEffect(() => {
    if (!DOMAIN || !hasConsent()) return;
    window.plausible?.("pageview");
  }, [location.pathname, location.search]);

  return null;
}
